import React, {useState} from "react"
import {isEqual} from "lodash"
import moment from "moment-timezone"

import authentication from "@irati/auth"
import GenericModal from "@irati/ui/src/GenericModal"
import modalStory, {anyStoriesOpen} from "@irati/utils/modalStory"
import apps, {RECOGNITION} from "@irati-web/app/src/routeConfig"
import {RouteComponentProps} from "react-router-dom"
import {injectIntl} from "@irati/utils/lang"
import {TEAMS_OPTION, EVERYONE_OPTION} from "@irati/recognition/constants/share"
import tracking, {LaunchRecognitionProps} from "@irati-tracking/recognition/recognition"

import {NewRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import {Recognition} from "@irati/types"
import {CancelConfirm} from "@irati-web/recognition/src/components/Popup/shared"
import History, {appOrContext} from "@irati/utils/history"
import {parseScheduledRecognitionToRecognition} from "@irati/recognition/utils/parseScheduledRecognition"
import {FeedItemType} from "@irati/recognition/enums"
import RewardRecognition from "@irati-web/recognition/src/components/Popup/RewardRecognition"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import {Trigger, useContentCardActions} from "@irati/braze"
import {RewardInfo} from "../RewardRecognition/RewardRecognitionBanner"
import {RecognitionRoutes} from "../../../enums/Routes"
import Create from "./Create"
import Success from "./Success"
import SuccessManager from "./SuccessManager"
import SuccessScheduled from "./SuccessScheduled"
import SuccessScheduledReward from "./SuccessScheduledReward"

export const CREATE = "CREATE"
export const CANCEL_CONFIRM = "CANCEL_CONFIRM"
export const SUCCESS = "SUCCESS"
export const SUCCESS_SCHEDULED = "SUCCESS_SCHEDULED"
export const SUCCESS_SCHEDULED_REWARD = "SUCCESS_SCHEDULED_REWARD"
export const REWARD_RECOGNITION = "REWARD_RECOGNITION"
export const SUCCESS_MANAGER = "SUCCESS_MANAGER"

const modals = [CREATE, CANCEL_CONFIRM, SUCCESS, REWARD_RECOGNITION, SUCCESS_MANAGER, SUCCESS_SCHEDULED, SUCCESS_SCHEDULED_REWARD]

export const EMPTY_RECOGNITION = (formatMessage: FormatMessage): NewRecognition => {
  const canShareWithEveryone = authentication.apps("recognition.recognition.everyone")
  return {
    companyValues: [],
    userIds: [],
    teamIds: [],
    message: "",
    isPrivate: true,
    customRewardId: null,
    share: canShareWithEveryone ? EVERYONE_OPTION(formatMessage) : TEAMS_OPTION(formatMessage)
  }
}
const getDefaultBudgetId = (rewardInfo: RewardInfo, unassignedBudgetId?: string) => {
  if (rewardInfo.budgetId) {
    return rewardInfo.budgetId
  }
  if (rewardInfo.pointsPerUser > 0) {
    return unassignedBudgetId
  }
  return null
}
interface Props extends InjectIntlProps, RouteComponentProps<{}, {}, {isFirstRecognition?: boolean; contentCardTrigger?: Trigger}> {
  scheduledRecognition?: Recognition
  hasRedirectToPolls?: boolean
  onHide?: () => void
}
export interface ScheduledOptions {
  date: string
  timezone: string
}

const CreateRecognitionPopup: React.FC<Props> = (props) => {
  const {formatMessage, scheduledRecognition, hasRedirectToPolls = true, onHide} = props
  const {contentCardTrigger} = props.location?.state || {}
  const [isFirstRecognition, setIsFirstRecognition] = useState(props.location?.state?.isFirstRecognition)
  const startingRecognition = {
    ...EMPTY_RECOGNITION(formatMessage),
    ...(scheduledRecognition ? parseScheduledRecognitionToRecognition(scheduledRecognition, formatMessage) : {})
  }
  const {data: budgetData} = useBudgetsList()
  const {markContentCardsDismissed, markContentCardClicked} = useContentCardActions()
  const [recognition, setRecognition] = useState<NewRecognition | Recognition>(startingRecognition)
  const initialRewardInfoState = {
    pointsPerUser: startingRecognition?.rewardAmount,
    budgetId: startingRecognition?.budget?.categoryId || null,
    useUnassigned: !!startingRecognition?.budget?.useUnassigned,
    customRewardId: startingRecognition?.customRewardId || null
  }
  const [rewardInfo, setRewardInfo] = useState<RewardInfo>(initialRewardInfoState)
  const initialScheduledOptionsState = {
    date: startingRecognition?.scheduledAt ? new Date(startingRecognition?.scheduledAt * 1000) : "",
    timezone: startingRecognition?.originalTimezone || ""
  }
  const [scheduledOptions, setScheduledOptions] = useState<ScheduledOptions>(initialScheduledOptionsState)
  const [show, setShow] = useState(modalStory(modals, CREATE))
  const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")
  // looks for any active popups, if none; we know its a new instance of popup so clear recognition
  const shouldClearRecognition = () => !anyStoriesOpen(show)
  const createAgain = () => {
    if (show.SUCCESS_MANAGER) {
      tracking.createNewRecognition({type: "reward"})
    } else {
      tracking.createNewRecognition({type: canReward ? "manager_recognition" : "user_recognition"})
    }
    if (scheduledRecognition) {
      History.push(appOrContext({to: RecognitionRoutes.CREATE_RECOGNITION, app: apps[RECOGNITION]}, {}))
      return
    }
    setRewardInfo({
      pointsPerUser: null,
      budgetId: null,
      useUnassigned: null,
      customRewardId: null
    })
    setScheduledOptions({
      date: null,
      timezone: null
    })
    setIsFirstRecognition(false)
    setRecognition(EMPTY_RECOGNITION(formatMessage))
    setShow(modalStory(modals, CREATE, show))
  }
  const showModal = (modalToShow = CREATE) => {
    const newRecognition = shouldClearRecognition() ? EMPTY_RECOGNITION(formatMessage) : recognition
    setRecognition(newRecognition)
    setShow(modalStory(modals, modalToShow, show))
  }
  const hide = () => {
    sendCancelEvent()
    setShow(modalStory(modals, null))
    contentCardTrigger && markContentCardsDismissed([contentCardTrigger.id])
    if (hasRedirectToPolls) {
      History.replace(appOrContext({to: "/polls", app: apps[RECOGNITION]}, {}))
    }
    onHide?.()
  }
  const sendCancelEvent = () => {
    if (scheduledRecognition) {
      return
    }
    if (show.CREATE || show.CANCEL_CONFIRM) {
      tracking.recognitionCtaClick({
        source: "recognition_form",
        type: canReward ? "manager_recognition_cancel" : "user_recognition_cancel"
      })
    } else if (show.SUCCESS || show.REWARD_RECOGNITION || show.SUCCESS_MANAGER) {
      tracking.recognitionCtaClick({
        source: "confirmation_modal",
        type: show.SUCCESS_MANAGER ? "reward_cancel" : canReward ? "manager_recognition_cancel" : "user_recognition_cancel"
      })
    }
  }
  const onViewFeed = () => {
    tracking.recognitionCtaClick({
      source: "confirmation_modal",
      type: "user_recognition_view_feed"
    })
    setShow(modalStory(modals, null))
    History.replace(appOrContext({to: "/polls", app: apps[RECOGNITION]}, {}))
    onHide?.()
  }
  const cancelConfirm = () => {
    const initialRewardInfoStateToCompare = {
      ...initialRewardInfoState,
      budgetId: getDefaultBudgetId(initialRewardInfoState, budgetData?.budgets.unassigned.uuid)
    }
    if (!isEqual(rewardInfo, initialRewardInfoStateToCompare)) {
      return showModal(CANCEL_CONFIRM)
    }
    if (
      (!!scheduledOptions.date || !!initialScheduledOptionsState.date) &&
      !moment
        .tz(scheduledOptions.date, scheduledOptions.timezone)
        .isSame(moment.tz(initialScheduledOptionsState.date, initialScheduledOptionsState.timezone))
    ) {
      return showModal(CANCEL_CONFIRM)
    }
    if (scheduledRecognition && isEqual(recognition, startingRecognition)) {
      return hide()
    }
    if (isEqual(recognition, EMPTY_RECOGNITION(formatMessage))) {
      return hide()
    }
    showModal(CANCEL_CONFIRM)
  }
  const onRecognitionChange = (newRecognition: NewRecognition | Recognition) => {
    setRecognition(newRecognition)
  }
  const onRecognitionCreated = (args: LaunchRecognitionProps) => {
    contentCardTrigger && markContentCardClicked(contentCardTrigger.id, {actioned: true})
    tracking.launchRecognition(args)
  }
  const onProcessComplete = (newRecognition: Recognition, isScheduled = false) => {
    setScheduledOptions(initialScheduledOptionsState)
    if (isScheduled) {
      showModal(newRecognition.rewardAmount ? SUCCESS_SCHEDULED_REWARD : SUCCESS_SCHEDULED)
      return
    }
    showModal((newRecognition as Recognition).walletTransferId ? SUCCESS_MANAGER : SUCCESS)
  }
  const onRewardCreated = () => {
    tracking.rewardCreatedOnRecognition({
      type: "recognition"
    })
    showModal(SUCCESS_MANAGER)
  }
  const onErrorReward = () => {
    tracking.rewardErrorOnRecognition({
      source: "manager_recognition",
      type: "confirmation_modal"
    })
  }
  return (
    <>
      <GenericModal show={show[CREATE]} onHide={cancelConfirm} customWidth="724" hideActions>
        <Create
          selectScreen={showModal}
          onRecognitionCreated={onRecognitionCreated}
          onProcessComplete={onProcessComplete}
          onErrorReward={onErrorReward}
          onRecognitionChange={onRecognitionChange}
          scheduledRecognition={scheduledRecognition}
          recognition={recognition as NewRecognition}
          isFirstRecognition={isFirstRecognition}
          rewardInfo={rewardInfo}
          setRewardInfo={setRewardInfo}
          scheduledOptions={scheduledOptions}
          setScheduledOptions={setScheduledOptions}
        />
      </GenericModal>
      <GenericModal show={show[REWARD_RECOGNITION]} customWidth="750" onHide={hide} hideActions>
        <RewardRecognition
          onErrorReward={onErrorReward}
          onClickButton={createAgain}
          onRewardCreated={onRewardCreated}
          recognition={recognition as Recognition}
          newRecognition
        />
      </GenericModal>
      <GenericModal show={show[SUCCESS]} onHide={hide} hideActions>
        <Success onClickButton={createAgain} onViewFeed={onViewFeed} onHide={hide} isFirstRecognition={isFirstRecognition} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_MANAGER]} onHide={hide} hideActions>
        <SuccessManager onClickButton={createAgain} onHide={hide} isNew type={FeedItemType.RECOGNITION} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_SCHEDULED]} onHide={hide} hideActions>
        <SuccessScheduled onClickButton={createAgain} onHide={hide} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_SCHEDULED_REWARD]} onHide={hide} hideActions>
        <SuccessScheduledReward onClickButton={createAgain} onHide={hide} />
      </GenericModal>
      <GenericModal show={show[CANCEL_CONFIRM]} critical hideActions>
        <CancelConfirm previous={show.previous} selectScreen={showModal} onHide={hide} />
      </GenericModal>
    </>
  )
}

export default injectIntl(CreateRecognitionPopup)
